import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import jApi, { Gif } from 'jamespot-user-api';
import { useAppDispatch } from '../../../redux/store';
import { Toast } from 'jamespot-front-business';
import { useAbortController } from '../../../hooks';

const GifsSelector = commonComponentsRegistry.getLazyComponent('GifsSelector');
const Modal = commonComponentsRegistry.getLazyComponent('Modal');

export type GifsModalProps = {
    onSelect: (url: string) => void;
    open: boolean;
    close: () => void;
};

const nbItemsPerPage = 10;

export const GifsModal = ({ onSelect, open, close }: GifsModalProps) => {
    const dispatch = useAppDispatch();
    const abortController = useAbortController();
    const [loading, setLoading] = useState(false);
    const [gifs, setGifs] = useState<Gif[]>([]);
    const [query, setQuery] = useState<{ q?: string; offset: number }>({ q: undefined, offset: 0 });

    const doClose = useCallback(() => {
        close();
        setQuery({ q: undefined, offset: 0 });
    }, [close]);

    useEffect(() => {
        setLoading(true);
        jApi.jamespot
            .getGifs(
                { q: query.q || 'awesome', limit: nbItemsPerPage, offset: query.offset },
                { signal: abortController.signal },
            )
            .then((res) => {
                setGifs(query.offset === 0 ? res.result : (prev) => [...prev, ...res.result]);
            })
            .catch((e) => {
                if (!e.aborted) {
                    dispatch(Toast.actions.error({ label: e.errorMsg ?? 'GLOBAL_Error' }));
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [abortController, dispatch, query]);

    return (
        <Modal
            title={'Gif'}
            open={open}
            closeHandler={doClose}
            enableClickAwayCloseModal
            variant={'fixed-height'}
            buttons={[
                {
                    children: <FormattedMessage id="GLOBAL_Cancel" />,
                    color: 'cancel',
                    clickHandler: doClose,
                },
            ]}>
            <GifsSelector
                gifs={gifs}
                onSelectGif={onSelect}
                loading={loading}
                onSearch={(q) => setQuery({ q, offset: 0 })}
                onReachEnd={() => {
                    setQuery((prev) => ({ ...prev, offset: prev.offset + nbItemsPerPage }));
                }}
            />
        </Modal>
    );
};
